<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 500));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(135deg, #fce7f3 0%, #ddd6fe 50%, #cffafe 100%)",
      fontFamily: props.block.fontFamily || "'Plus Jakarta Sans', sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.4}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 28}px`,
  };
});
</script>

<template>
  <div class="cd-blob" :style="containerStyle">
    <div class="cd-blob__bg cd-blob__bg--a" />
    <div class="cd-blob__bg cd-blob__bg--b" />
    <div class="cd-blob__bg cd-blob__bg--c" />

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-blob__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 700}ms`,
      }"
    >
      <span
        class="cd-blob__digit"
        :key="seg.value"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#1f2937',
        }"
      >
        {{ seg.value }}
      </span>
      <span
        class="cd-blob__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || '#6b7280',
        }"
      >
        {{ seg.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-blob {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 22px;
  overflow: hidden;
  isolation: isolate;
}
.cd-blob__bg {
  position: absolute;
  border-radius: 50%;
  filter: blur(50px);
  opacity: 0.55;
  z-index: 0;
}
.cd-blob__bg--a {
  width: 280px;
  height: 280px;
  background: #f0abfc;
  top: -60px;
  left: -40px;
  animation: cd-blob-float 12s ease-in-out infinite;
}
.cd-blob__bg--b {
  width: 240px;
  height: 240px;
  background: #93c5fd;
  bottom: -50px;
  right: -30px;
  animation: cd-blob-float 16s ease-in-out infinite reverse;
}
.cd-blob__bg--c {
  width: 200px;
  height: 200px;
  background: #fde68a;
  top: 20%;
  left: 35%;
  animation: cd-blob-float 18s ease-in-out infinite;
}
@keyframes cd-blob-float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -25px) scale(1.08);
  }
  66% {
    transform: translate(-25px, 25px) scale(0.94);
  }
}
.cd-blob__cell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow:
    0 14px 40px -10px rgba(60, 60, 120, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  animation: cd-blob-bob 6s ease-in-out infinite;
  transition: transform 0.4s ease;
}
.cd-blob__cell:hover {
  transform: translateY(-3px);
}
@keyframes cd-blob-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}
.cd-blob__digit {
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  display: inline-block;
  animation: cd-blob-tick var(--cd-tick, 500ms) cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cd-blob-tick {
  0% {
    opacity: 0;
    transform: scale(0.7);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
.cd-blob__label {
  margin-top: 6px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
</style>
